// frontend/src/App.js
import React, { useState, useContext, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import OneSignal from 'react-onesignal';
import axios from 'axios';
import routes, { renderRoutes } from './routes';
import { BASENAME, ONESIGNAL_APP_ID } from './config/constant';
import 'react-toastify/dist/ReactToastify.css';
import { InjectAxiosInterceptors } from './utils/axios';
import { ConfigContext } from './contexts/ConfigContext';
import * as actionType from './store/actions';

const App = () => {
    console.log('APP STARTED');
    const { state, dispatch } = useContext(ConfigContext);
    const account = useSelector((state) => state.account);
    const [initialized, setInitialized] = useState(false);
    
    // --- Main OneSignal Initialization Effect ---
    useEffect(() => {
        const initializeOneSignal = async () => {
            if (!account.token || initialized) return;

            try {
                await OneSignal.init({
                    appId: ONESIGNAL_APP_ID,
                    notifyButton: {
                        enable: true,
                        size: 'small',
                        position: 'bottom-left'
                    },
                    inFocusDisplaying: 'notification',
                    autoResubscribe: false,
                    // path: `${window.location.protocol}//${window.location.host}`,
                    serviceWorkerPath: 'service-worker.js',
                    allowLocalhostAsSecureOrigin: true,
                    // Note: Other params like serviceWorkerPath are often auto-managed
                });

                setInitialized(true); // Set initialized right after .init()

                // The subscription listener (in the next useEffect) will handle the rest
            } catch (error) {
                console.error('Error initializing OneSignal:', error);
            }
        };

        initializeOneSignal();
    }, [account?.token, initialized]);
    
    // --- OneSignal Subscription Synchronization Effect ---
    useEffect(() => {
        if (initialized) {
            const subscriptionChangeListener = async (change) => {
                const pushId = change.current.id;
                const isOptedIn = change.current.optedIn;
                const userId = account?.user?.id;

                if (!userId) return; // Safety check

                try {
                    // If the user is opted in and has a subscription ID, save it
                    if (isOptedIn && pushId) {
                        await axios.put(`auth/user/${userId}/push/${pushId}`);
                        console.debug('Push subscription ID updated on backend.');
                    // If the user is opted out, remove the subscription
                    } else {
                        // The previous pushId is available in `change.previous.id`
                        const idToRemove = pushId || change.previous.id;
                        if (idToRemove) {
                            await axios.delete(`auth/user/${userId}/push/${idToRemove}`);
                            console.debug('Push subscription removed from backend.');
                        }
                    }
                } catch (error) {
                    console.error('Error syncing push subscription with backend:', error);
                }
            };

            // Listen for subscription changes
            OneSignal.User.PushSubscription.addEventListener('change', subscriptionChangeListener);

            // Clean up the listener when the component unmounts
            return () => {
                OneSignal.User.PushSubscription.removeEventListener('change', subscriptionChangeListener);
            };
        }
    }, [initialized]); // Dependency array is key for this to work

    // --- Other Effects (Unchanged) ---
    useEffect(() => {
        const layoutType = localStorage.getItem('layoutType');
        if (layoutType) {
            dispatch({ type: actionType.LAYOUT_TYPE, layoutType });
        }
    }, [dispatch]);

    useEffect(() => {
        if (state.layoutType === 'dark') {
            document.body.classList.add('dark-theme');
            document.body.classList.add('datta-dark');
        } else {
            document.body.classList.remove('dark-theme');
            document.body.classList.remove('datta-dark');
        }
    }, [state.layoutType]);

    return (
        <>
            <Router basename={BASENAME}>
                <InjectAxiosInterceptors />
                {renderRoutes(routes)}
            </Router>
            <ToastContainer theme={state.layoutType === 'dark' ? 'dark' : 'light'} />
        </>
    );
};

export default App;